import { Router, type IRouter } from "express";
import OpenAI from "openai";
import { db, dealsTable, usersTable, sellerLeadsTable } from "@workspace/db";
import { eq } from "drizzle-orm";
import { AnalyzeDealBody, AnalyzeSellerLeadBody, BrokerAssistBody } from "@workspace/api-zod";
import { requireAuth, requireRole } from "../middlewares/auth";
import { logger } from "../lib/logger";

const router: IRouter = Router();

const openai = process.env.OPENAI_API_KEY
  ? new OpenAI({ apiKey: process.env.OPENAI_API_KEY })
  : null;

// ─── Deal Analysis ────────────────────────────────────────────────────────────

interface DealAnalysisOutput {
  distress_score: number;
  deal_score: number;
  investor_appeal: "High" | "Medium" | "Low";
  risk_level: "High" | "Medium" | "Low";
  recommended_action: "Prioritize" | "Verify" | "Reject";
  investor_summary: string;
  risk_flags: string;
  recommended_buyer_type: string;
  liquidity_rating: "Low" | "Medium" | "High";
  negotiation_angle: string;
  missing_info: string;
  pricing_comment: string;
  admin_decision: "Approve" | "Review" | "Reject";
}

async function runDealAnalysis(deal: typeof dealsTable.$inferSelect): Promise<DealAnalysisOutput> {
  const prompt = `You are a senior Dubai real estate investment analyst for VaultDXB, a private off-market distressed property marketplace. Analyse this property deal and return a JSON object with exactly these keys:

- distress_score: integer 0–100 (100 = maximum distress/urgency)
- deal_score: integer 0–100 comprehensive investment score based on: discount below market (25pts), location demand (20pts), price vs market (20pts), urgency/motivation (15pts), rental yield/ROI (10pts), deal verification strength (10pts)
- investor_appeal: exactly one of "High", "Medium", "Low"
- risk_level: exactly one of "High", "Medium", "Low"
- recommended_action: exactly one of "Prioritize", "Verify", "Reject"
- investor_summary: 2–3 sentence investor-friendly summary. Use language like "estimated", "potential". Do not guarantee ROI.
- risk_flags: comma-separated list of 3–5 potential risk factors
- recommended_buyer_type: e.g. "Cash-ready end user", "Short-term flipper", "Long-term rental investor"
- liquidity_rating: exactly one of "Low", "Medium", "High"
- negotiation_angle: 1–2 sentence negotiation strategy for a buyer
- missing_info: comma-separated list of 2–4 pieces of information missing from the deal that would strengthen the listing (e.g. "Title deed status", "NOC availability", "Structural condition report")
- pricing_comment: 1 sentence admin pricing observation (e.g. "Asking price aligns with Q1 2024 comparable sales — acceptable" or "Market value appears optimistic for this sub-community")
- admin_decision: exactly one of "Approve", "Review", "Reject" — suggested admin action based on deal quality and completeness

Property data:
- Title: ${deal.title}
- Area: ${deal.area}
- Building: ${deal.building ?? "N/A"}
- Unit Type: ${deal.unitType ?? "N/A"}
- Bedrooms: ${deal.bedrooms ?? "N/A"}
- Size: ${deal.sizeSqft ?? "N/A"} sqft
- Asking Price: AED ${deal.askingPrice}
- Estimated Market Price: AED ${deal.estimatedMarketPrice}
- Discount: ${deal.discountPercentage ?? "N/A"}%
- ROI / Rental Yield: ${deal.roiYield ?? "Not provided"}%
- Developer: ${deal.developer ?? "N/A"}
- Handover: ${deal.handoverStatus ?? "N/A"}
- Seller Type: ${deal.sellerType ?? "Not disclosed"}
- Deal Category: ${deal.dealCategory ?? "N/A"}
- Seller Timeline: ${deal.sellerTimeline ?? "Not specified"}
- Urgency Reason: ${deal.urgencyReason ?? "Not disclosed"}
- Payment Terms: ${deal.paymentPlanDetails ?? "Unknown"}
- Property Status: ${deal.propertyStatus ?? "Unknown"}

Respond with only valid JSON. No markdown, no code fences.`;

  if (!openai) {
    logger.warn("OPENAI_API_KEY not set — returning mock deal analysis");
    return mockDealAnalysis(deal);
  }

  const response = await openai.chat.completions.create({
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: prompt }],
    temperature: 0.3,
    max_tokens: 900,
  });

  const text = response.choices[0]?.message?.content ?? "{}";
  try {
    return JSON.parse(text) as DealAnalysisOutput;
  } catch {
    logger.error({ text }, "Failed to parse OpenAI deal response");
    return mockDealAnalysis(deal);
  }
}

function mockDealAnalysis(deal: typeof dealsTable.$inferSelect): DealAnalysisOutput {
  const discount = parseFloat(deal.discountPercentage ?? "0");
  const roiYield = deal.roiYield ? parseFloat(deal.roiYield) : 0;
  const distress = Math.min(Math.round(discount * 2 + 20), 100);

  // Comprehensive deal score
  const discountPoints = Math.min(discount * 1.5, 25);
  const locationPoints = ["Palm Jumeirah", "Downtown Dubai", "Dubai Marina", "Business Bay"].some(
    a => deal.area?.includes(a)
  ) ? 20 : 12;
  const pricePoints = discount >= 20 ? 20 : discount >= 10 ? 14 : 8;
  const urgencyPoints = deal.urgencyReason ? 12 : 6;
  const roiPoints = roiYield >= 8 ? 10 : roiYield >= 6 ? 7 : roiYield >= 4 ? 4 : 2;
  const verificationPoints = deal.brokerContact && deal.propertyStatus ? 10 : 5;
  const dealScore = Math.min(Math.round(discountPoints + locationPoints + pricePoints + urgencyPoints + roiPoints + verificationPoints), 100);

  const appeal: "High" | "Medium" | "Low" = dealScore >= 70 ? "High" : dealScore >= 45 ? "Medium" : "Low";
  const riskLevel: "High" | "Medium" | "Low" = discount < 10 ? "High" : discount < 20 ? "Medium" : "Low";
  const action: "Prioritize" | "Verify" | "Reject" = dealScore >= 70 ? "Prioritize" : dealScore >= 45 ? "Verify" : "Reject";
  const adminDecision: "Approve" | "Review" | "Reject" = dealScore >= 65 ? "Approve" : dealScore >= 40 ? "Review" : "Reject";

  return {
    distress_score: distress,
    deal_score: dealScore,
    investor_appeal: appeal,
    risk_level: riskLevel,
    recommended_action: action,
    investor_summary: `This estimated ${discount}% below-market asset in ${deal.area} presents a potential acquisition opportunity. The seller's motivation suggests urgency, which may allow for favourable terms — however, all figures require independent verification before committing capital.`,
    risk_flags: "Mortgage encumbrance unknown, Title transfer timeline unconfirmed, Market comparables require validation, Seller motivation unverified, Structural condition not assessed",
    recommended_buyer_type: discount >= 20 ? "Cash-ready end user or short-term flipper" : "Long-term rental investor",
    liquidity_rating: discount >= 25 ? "High" : discount >= 15 ? "Medium" : "Low",
    negotiation_angle: `Given the seller's apparent time pressure, a clean cash offer with a 7–14 day transfer window may secure an additional 2–3% discount beyond the listed price. Verify title and NOC status before proceeding.`,
    missing_info: "Title deed status, NOC availability, Structural condition report, Mortgage outstanding balance",
    pricing_comment: discount >= 20
      ? `Discount of ${discount}% appears genuine — pricing is competitive relative to recent comparable transactions in ${deal.area}.`
      : `Discount of ${discount}% is modest — admin should verify comparable sales to confirm market value estimate.`,
    admin_decision: adminDecision,
  };
}

// ─── Broker Assist ────────────────────────────────────────────────────────────

async function runBrokerAssist(
  data: {
    title: string; area: string; unitType?: string; bedrooms?: number;
    bathrooms?: number; sizeSqft?: number; askingPrice: number;
    estimatedMarketPrice: number; urgencyReason?: string; sellerTimeline?: string;
    propertyStatus?: string;
  },
  mode: "improve_description" | "generate_pitch" | "check_attractiveness"
): Promise<{ result: string; attractivenessScore?: number }> {
  const discount = ((data.estimatedMarketPrice - data.askingPrice) / data.estimatedMarketPrice * 100).toFixed(1);
  const dealContext = `Title: ${data.title} | Area: ${data.area} | Type: ${data.unitType ?? "N/A"} | ${data.bedrooms ?? "N/A"} BR | ${data.sizeSqft ?? "N/A"} sqft | Asking: AED ${data.askingPrice.toLocaleString()} | Market: AED ${data.estimatedMarketPrice.toLocaleString()} | Discount: ${discount}% | Urgency: ${data.urgencyReason ?? "Not specified"} | Timeline: ${data.sellerTimeline ?? "Not specified"} | Status: ${data.propertyStatus ?? "Not specified"}`;

  if (!openai) {
    return mockBrokerAssist(data, mode, parseFloat(discount));
  }

  let prompt = "";
  if (mode === "improve_description") {
    prompt = `You are a luxury real estate copywriter for VaultDXB, a private Dubai off-market marketplace. Write an improved, professional deal description for this property that will appeal to sophisticated investors. Keep it concise (3–4 sentences), confidential in tone, and highlight the opportunity without making guarantees. Do not use emojis.\n\nDeal: ${dealContext}\n\nReturn only the improved description text.`;
  } else if (mode === "generate_pitch") {
    prompt = `You are a senior real estate investment advisor at VaultDXB. Generate a compelling investor pitch for this off-market deal. Structure it as: (1) The Opportunity (1 sentence), (2) The Financials (2–3 bullet points with key numbers), (3) The Edge (why this deal is unique or urgent), (4) Suggested Next Step (1 sentence). Keep it concise and institutional in tone. No emojis.\n\nDeal: ${dealContext}\n\nReturn the pitch text only.`;
  } else {
    prompt = `You are a Dubai real estate investment analyst. Rate the attractiveness of this deal for a sophisticated investor and return a JSON object with exactly two keys:\n- score: integer 0–100 (overall investor attractiveness)\n- analysis: 3–4 sentences explaining the score, key strengths, and concerns\n\nDeal: ${dealContext}\n\nRespond with only valid JSON. No markdown.`;
  }

  const response = await openai.chat.completions.create({
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: prompt }],
    temperature: 0.4,
    max_tokens: 500,
  });

  const text = (response.choices[0]?.message?.content ?? "").trim();

  if (mode === "check_attractiveness") {
    try {
      const parsed = JSON.parse(text) as { score: number; analysis: string };
      return { result: parsed.analysis, attractivenessScore: parsed.score };
    } catch {
      return { result: text };
    }
  }

  return { result: text };
}

function mockBrokerAssist(
  data: { title: string; area: string; unitType?: string; askingPrice: number; estimatedMarketPrice: number; urgencyReason?: string },
  mode: "improve_description" | "generate_pitch" | "check_attractiveness",
  discount: number
): { result: string; attractivenessScore?: number } {
  if (mode === "improve_description") {
    return {
      result: `An exceptional off-market opportunity in ${data.area} — a ${data.unitType ?? "premium unit"} available at an estimated ${discount}% below current market value. The seller's motivation creates a rare window for serious investors to transact at favourable terms. All details are strictly confidential and shared exclusively with qualified VaultDXB members. Independent verification is recommended prior to transacting.`,
    };
  } else if (mode === "generate_pitch") {
    return {
      result: `**The Opportunity**\nA rare below-market ${data.unitType ?? "asset"} in ${data.area}, offered exclusively through VaultDXB's private network.\n\n**The Financials**\n• Asking Price: AED ${data.askingPrice.toLocaleString()}\n• Estimated Market Value: AED ${data.estimatedMarketPrice.toLocaleString()}\n• Estimated Discount: ${discount}%\n\n**The Edge**\n${data.urgencyReason ? `Seller motivation: ${data.urgencyReason}. ` : ""}This deal is not on the open market and is available to a limited number of pre-qualified investors.\n\n**Suggested Next Step**\nRequest full deal access through VaultDXB to receive broker contact details and documentation.`,
    };
  } else {
    const score = Math.min(Math.round(discount * 2.5 + 30), 100);
    return {
      result: `This deal scores ${score}/100 for investor attractiveness, driven primarily by its estimated ${discount}% discount to market value in ${data.area}. ${score >= 65 ? "The combination of location demand and seller urgency makes this a strong candidate for both capital appreciation and rental income strategies." : "While the discount is present, further verification of the market value estimate and seller motivation would strengthen the investment case."} ${data.urgencyReason ? "The seller's stated urgency suggests genuine motivation, which may support price negotiations." : "More information on seller motivation would be beneficial."} Proceed with due diligence and independent valuation before committing capital.`,
      attractivenessScore: score,
    };
  }
}

// ─── Seller Lead Analysis ─────────────────────────────────────────────────────

interface LeadAnalysisOutput {
  lead_temperature: "Hot" | "Warm" | "Cold";
  urgency_score: number;
  likely_motivation: string;
  suggested_agent_script: string;
  recommended_next_step: string;
}

async function runLeadAnalysis(lead: typeof sellerLeadsTable.$inferSelect): Promise<LeadAnalysisOutput> {
  const prompt = `You are a senior Dubai real estate advisor at VaultDXB. Qualify this urgent sale seller lead and return a JSON object with exactly these keys:

- lead_temperature: exactly one of "Hot", "Warm", "Cold"
- urgency_score: integer 0–100 (100 = most urgent seller)
- likely_motivation: 1–2 sentences on the estimated root cause of the seller's urgency
- suggested_agent_script: 2–3 sentence script for the agent's opening call
- recommended_next_step: concrete next action for the assigned agent

Seller lead data:
- Property Area: ${lead.propertyArea}
- Building/Project: ${lead.buildingProject ?? "Not specified"}
- Property Type: ${lead.propertyType}
- Bedrooms: ${lead.bedrooms ?? "N/A"}
- Asking Price: AED ${lead.askingPrice}
- Mortgage Outstanding: ${lead.mortgageOutstanding ?? "None / Unknown"}
- Urgency Reason: ${lead.urgencyReason}
- Timeline to Sell: ${lead.timelineToSell}
- Additional Notes: ${lead.additionalNotes ?? "None"}

Respond with only valid JSON. No markdown, no code fences.`;

  if (!openai) {
    logger.warn("OPENAI_API_KEY not set — returning mock lead analysis");
    return mockLeadAnalysis(lead);
  }

  const response = await openai.chat.completions.create({
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: prompt }],
    temperature: 0.3,
    max_tokens: 500,
  });

  const text = response.choices[0]?.message?.content ?? "{}";
  try {
    return JSON.parse(text) as LeadAnalysisOutput;
  } catch {
    logger.error({ text }, "Failed to parse OpenAI lead response");
    return mockLeadAnalysis(lead);
  }
}

function mockLeadAnalysis(lead: typeof sellerLeadsTable.$inferSelect): LeadAnalysisOutput {
  const hasMortgage = !!lead.mortgageOutstanding && parseFloat(lead.mortgageOutstanding) > 0;
  const urgencyKeywords = ["urgent", "immediate", "divorce", "financial", "mortgage", "foreclosure", "distress"];
  const isUrgent = urgencyKeywords.some(k => lead.urgencyReason.toLowerCase().includes(k));

  return {
    lead_temperature: isUrgent ? "Hot" : hasMortgage ? "Warm" : "Cold",
    urgency_score: isUrgent ? 82 : hasMortgage ? 55 : 30,
    likely_motivation: hasMortgage
      ? `Seller likely facing mortgage servicing pressure${isUrgent ? " combined with a personal urgency driver" : ""}. A distressed sale at below-market value is probable.`
      : `Seller motivation appears to be a lifestyle or relocation factor. Price sensitivity may be moderate.`,
    suggested_agent_script: `"Good morning, I'm calling from VaultDXB regarding your property in ${lead.propertyArea}. We have a network of serious, pre-qualified investors who can move quickly on the right asset. Could we schedule a brief valuation call to understand your ideal timeline?"`,
    recommended_next_step: `Call within 24 hours. Confirm property ownership, outstanding mortgage balance, and willingness to accept offers at estimated ${hasMortgage ? "10–20%" : "5–10%"} below current market value. Send a WhatsApp follow-up immediately after the call.`,
  };
}

// ─── Routes ───────────────────────────────────────────────────────────────────

router.post("/ai/analyze-deal", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const parsed = AnalyzeDealBody.safeParse(req.body);
  if (!parsed.success) {
    res.status(400).json({ error: parsed.error.message });
    return;
  }

  const { dealId } = parsed.data;
  const [deal] = await db.select().from(dealsTable).where(eq(dealsTable.id, dealId));
  if (!deal) {
    res.status(404).json({ error: "Deal not found" });
    return;
  }

  try {
    const analysis = await runDealAnalysis(deal);

    await db.update(dealsTable).set({
      aiDistressScore: analysis.distress_score,
      aiDealScore: analysis.deal_score,
      aiInvestorAppeal: analysis.investor_appeal,
      aiRiskLevel: analysis.risk_level,
      aiRecommendedAction: analysis.recommended_action,
      aiInvestorSummary: analysis.investor_summary,
      aiRiskFlags: analysis.risk_flags,
      aiRecommendedBuyerType: analysis.recommended_buyer_type,
      aiLiquidityRating: analysis.liquidity_rating,
      aiNegotiationAngle: analysis.negotiation_angle,
      aiMissingInfo: analysis.missing_info,
      aiPricingComment: analysis.pricing_comment,
      aiAdminDecision: analysis.admin_decision,
    }).where(eq(dealsTable.id, dealId));

    res.json({
      dealId,
      aiDistressScore: analysis.distress_score,
      aiDealScore: analysis.deal_score,
      aiInvestorAppeal: analysis.investor_appeal,
      aiRiskLevel: analysis.risk_level,
      aiRecommendedAction: analysis.recommended_action,
      aiInvestorSummary: analysis.investor_summary,
      aiRiskFlags: analysis.risk_flags,
      aiRecommendedBuyerType: analysis.recommended_buyer_type,
      aiLiquidityRating: analysis.liquidity_rating,
      aiNegotiationAngle: analysis.negotiation_angle,
      aiMissingInfo: analysis.missing_info,
      aiPricingComment: analysis.pricing_comment,
      aiAdminDecision: analysis.admin_decision,
    });
  } catch (err) {
    logger.error({ err }, "Deal AI analysis failed");
    res.status(500).json({ error: "AI analysis failed. Please try again." });
  }
});

router.post("/ai/broker-assist", requireAuth, requireRole("broker", "admin"), async (req, res): Promise<void> => {
  const parsed = BrokerAssistBody.safeParse(req.body);
  if (!parsed.success) {
    res.status(400).json({ error: parsed.error.message });
    return;
  }

  const { mode, ...dealData } = parsed.data;
  try {
    const result = await runBrokerAssist(
      {
        title: dealData.title,
        area: dealData.area,
        unitType: dealData.unitType,
        bedrooms: dealData.bedrooms,
        bathrooms: dealData.bathrooms,
        sizeSqft: dealData.sizeSqft,
        askingPrice: dealData.askingPrice,
        estimatedMarketPrice: dealData.estimatedMarketPrice,
        urgencyReason: dealData.urgencyReason,
        sellerTimeline: dealData.sellerTimeline,
        propertyStatus: dealData.propertyStatus,
      },
      mode as "improve_description" | "generate_pitch" | "check_attractiveness"
    );
    res.json({ mode, result: result.result, attractivenessScore: result.attractivenessScore ?? null });
  } catch (err) {
    logger.error({ err }, "Broker assist failed");
    res.status(500).json({ error: "AI assist failed. Please try again." });
  }
});

router.get("/ai/deals/:id/matched-investors", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const dealId = parseInt(raw, 10);

  const [deal] = await db.select().from(dealsTable).where(eq(dealsTable.id, dealId));
  if (!deal) {
    res.status(404).json({ error: "Deal not found" });
    return;
  }

  const askingPrice = parseFloat(deal.askingPrice);
  const discount = parseFloat(deal.discountPercentage ?? "0");

  // Fetch all approved investors
  const allInvestors = await db
    .select()
    .from(usersTable)
    .where(eq(usersTable.role, "investor"));

  const matched = allInvestors
    .filter(inv => inv.status === "approved")
    .map(inv => {
      let score = 0;

      // Membership tier match to deal value
      if (inv.membership === "elite") score += 40;
      else if (inv.membership === "vip") score += 25;
      else score += 10;

      // VIP-only deals: free-tier investors score 0
      if (deal.isVipOnly && inv.membership === "free") return null;

      // Budget heuristic by membership
      const budgetFit =
        inv.membership === "elite" ? (askingPrice <= 20_000_000 ? 30 : askingPrice <= 50_000_000 ? 20 : 10)
        : inv.membership === "vip" ? (askingPrice <= 8_000_000 ? 30 : askingPrice <= 15_000_000 ? 20 : 5)
        : askingPrice <= 3_000_000 ? 25 : askingPrice <= 6_000_000 ? 12 : 0;
      score += budgetFit;

      // Discount attractiveness — higher discount = better match for deal hunters
      score += Math.min(Math.round(discount * 0.6), 20);

      // Bonus for elite on high-discount deals
      if (inv.membership === "elite" && discount >= 20) score += 10;

      return {
        id: inv.id,
        name: inv.name,
        email: inv.email,
        membership: inv.membership,
        matchScore: Math.min(score, 100),
      };
    })
    .filter((inv): inv is NonNullable<typeof inv> => inv !== null)
    .sort((a, b) => b.matchScore - a.matchScore);

  res.json({
    dealId,
    matchedCount: matched.length,
    investors: matched.slice(0, 20),
  });
});

router.post("/ai/analyze-seller-lead", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const parsed = AnalyzeSellerLeadBody.safeParse(req.body);
  if (!parsed.success) {
    res.status(400).json({ error: parsed.error.message });
    return;
  }

  const { leadId } = parsed.data;
  const [lead] = await db.select().from(sellerLeadsTable).where(eq(sellerLeadsTable.id, leadId));
  if (!lead) {
    res.status(404).json({ error: "Seller lead not found" });
    return;
  }

  try {
    const analysis = await runLeadAnalysis(lead);

    await db.update(sellerLeadsTable).set({
      aiLeadTemperature: analysis.lead_temperature,
      aiUrgencyScore: analysis.urgency_score,
      aiLikelyMotivation: analysis.likely_motivation,
      aiSuggestedAgentScript: analysis.suggested_agent_script,
      aiRecommendedNextStep: analysis.recommended_next_step,
      updatedAt: new Date(),
    }).where(eq(sellerLeadsTable.id, leadId));

    res.json({
      leadId,
      aiLeadTemperature: analysis.lead_temperature,
      aiUrgencyScore: analysis.urgency_score,
      aiLikelyMotivation: analysis.likely_motivation,
      aiSuggestedAgentScript: analysis.suggested_agent_script,
      aiRecommendedNextStep: analysis.recommended_next_step,
    });
  } catch (err) {
    logger.error({ err }, "Lead AI analysis failed");
    res.status(500).json({ error: "AI analysis failed. Please try again." });
  }
});

export default router;
